import { ref } from "vue";
import { RetellWebClient } from "retell-client-js-sdk";
import { telephonyProviders } from "@/src/constants/telephonyConstants.js";
import BaseTelephonyDevice from "@/src/services/telephony/base/baseTelephonyDevice.js";
import VoiceAgentService from "@/src/api/VoiceAgentService.js";
import { callStatuses } from "@/src/constants/callConstants.js";
import store from "@/src/store";

class RetellDevice extends BaseTelephonyDevice {
  constructor() {
    super(telephonyProviders.retell);
    this.retellWebClient = null;
    this.isReady = ref(false);
    this.currentCall = ref(null);
    this.accessToken = null;
    this.callStatus = ref(callStatuses.IDLE);
    this.agentId = null;
    this.callId = null;
    this.eventHandler = null;
  }

  async initialize(eventHandler = null) {
    try {
      if (this.isReady.value) {
        return { success: true };
      }

      if (typeof window === "undefined") {
        throw new Error("Retell SDK requires browser environment");
      }
      
      if (!RetellWebClient) {
        throw new Error("Failed to load Retell SDK");
      }

      this.retellWebClient = new RetellWebClient();
      
      if (eventHandler) {
        this.eventHandler = eventHandler;
        await eventHandler.initialize(this);
      }
      
      this.isReady.value = true;

      return { success: true };
    } catch (error) {
      console.error("Failed to initialize Retell device:", error);
      this.isReady.value = false;
      throw error;
    }
  }

  async makeCall(params) {
    try {
      if (!this.isReady.value || !this.retellWebClient) {
        throw new Error("Retell device not initialized");
      }

      const { agentId } = params;
      
      if (!agentId) {
        throw new Error("Agent ID is required");
      }

      this.agentId = agentId;
      this.callStatus.value = callStatuses.CONNECTING;

      const response = await VoiceAgentService.createWebCall(agentId);

      if (!response?.success || !response?.access_token) {
        throw new Error(response?.error || "Failed to create web call");
      }

      this.accessToken = response.access_token;
      this.callId = response.call_id;

      await this.retellWebClient.startCall({
        accessToken: this.accessToken,
      });

      this.currentCall.value = {
        callId: this.callId,
        agentId: this.agentId,
        status: callStatuses.CONNECTED,
      };

      this.callStatus.value = callStatuses.CONNECTED;

      store.dispatch("voiceAgents/setCallOngoing", true);
      store.dispatch("voiceAgents/setCurrentAgentId", this.agentId);
      store.dispatch("voiceAgents/setCallStatus", callStatuses.CONNECTED);

      return { success: true, callId: this.callId };
    } catch (error) {
      console.error("Failed to make call:", error);
      this.callStatus.value = callStatuses.IDLE;
      
      store.dispatch("voiceAgents/setCallError", error.message);
      store.dispatch("voiceAgents/endTestCall");

      throw error;
    }
  }

  async hangupCall() {
    try {
      if (!this.retellWebClient) {
        return { success: false, error: "Device not initialized" };
      }

      this.retellWebClient.stopCall();

      this.currentCall.value = null;
      this.callStatus.value = callStatuses.IDLE;
      this.accessToken = null;
      this.callId = null;
      this.agentId = null;

      store.dispatch("voiceAgents/endTestCall");

      return { success: true };
    } catch (error) {
      console.error("Failed to hangup call:", error);
      throw error;
    }
  }

  getDeviceStatus() {
    return {
      isReady: this.isReady.value,
      callStatus: this.callStatus.value,
      currentCall: this.currentCall.value,
    };
  }

  getCurrentConnection() {
    return this.currentCall.value;
  }

  getIncomingConnection() {
    return null;
  }

  async cleanup() {
    try {
      const callStatus = store.getters["voiceAgents/callStatus"];
      
      if (this.retellWebClient && callStatus !== callStatuses.IDLE) {
        this.retellWebClient.stopCall();
      }

      this.retellWebClient = null;
      this.eventHandler = null;
      this.isReady.value = false;
      this.currentCall.value = null;
      this.accessToken = null;
      this.callStatus.value = callStatuses.IDLE;
      this.agentId = null;
      this.callId = null;

      store.dispatch("voiceAgents/endTestCall");

      return { success: true };
    } catch (error) {
      console.error("Failed to cleanup:", error);
      throw error;
    }
  }

  getStatus() {
    return {
      isReady: this.isReady.value,
      callStatus: this.callStatus.value,
      currentCall: this.currentCall.value,
      hasActiveDevice: !!this.retellWebClient,
    };
  }
}

export default new RetellDevice();

